import { PAListStore } from "@/Appointments/Stores/PAListStore";
import { Appointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { RealTimeNotificationResourceType } from "@/Common/Api/RealTimeNotification/Dto/RealTimeNotificationResourceType";
import { realTimeNotificationClient } from "@/Common/Api/RealTimeNotification/SignalRClient";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { SortDirection } from "@/Common/Models/UI/Enums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ServerEvent, ServerEventWithThen } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";

class PAListResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.Myle_SearchAppointments, this.SearchAppointments);
        this.RegisterOperation(ServerOperations.Server.Myle_PAList_PrintLabelCompleteOne, this.OpenLabelPDF);
        this.RegisterOperation(ServerOperations.Server.RAMQ_CheckSubscription, this.RAMQCheckSubscription);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_GenerateLabel, this.OpenLabelPDF);
        this.RegisterOperation(ServerOperations.Server.Myle_UpdateUserSettings, this.SaveFilterSettings);
    }

    private OpenLabelPDF(response: ServerEvent): void {
        const fileName = response.Data["FileName"];
        PrintHelper.ShowPrintPreviewFileName(fileName);
    }

    private SearchAppointments(response: ServerEvent, store: PAListStore): void {
        const appointments: Appointment[] = response.Data["Appointments"];
        const isDescending = store.PAListModel.get("OrderByDirection") === SortDirection.DESC;
        const lastColumnOrdered = store.PAListModel.get("LastColumnOrdered");

        const sortedAppointments = this.sortAppointments(appointments, lastColumnOrdered, isDescending);

        store.PAListModel = store.PAListModel.set("TableData", Immutable.List<Appointment>(sortedAppointments));

        if (cacheStore.ClinicSettings.EnableRealTimeNotifications)
            this.refreshRealTimeNotificationChannels(store);
    }

    private sortAppointments(appointments: Appointment[], column: string, isDescending: boolean): Appointment[] {
        if (!column)
            return appointments;

        switch (column) {
            case "StartTime":
                return ArrayHelper.SortDynamic(appointments, "StartTime", isDescending);
            case "PatientFirstName":
                return ArrayHelper.SortDynamic(appointments, "PatientLastName", isDescending);
            case "AppointmentStateLookupName":
                return this.sortByAppointmentState(appointments, isDescending);
            default:
                return appointments;
        }
    }

    private sortByAppointmentState(appointments: Appointment[], isDescending: boolean): Appointment[] {
        const directionChange = isDescending ? -1 : 1;

        return appointments.sort(a => {
            if (a.AppointmentStateLookupCode !== "COMPLETED") // "in waiting room"
                return 1 * directionChange;
            else
                return -1 * directionChange;
        });
    }

    private refreshRealTimeNotificationChannels(store: PAListStore): void {
        const providers = store.PADisplaySettingsModel.get("EmployeesFiltered").toArray().map(employee => employee.ContactId);
        realTimeNotificationClient.updateChannelsByProviders(RealTimeNotificationResourceType.Appointment, providers);
    }

    private RAMQCheckSubscription(response: ServerEvent, store: PAListStore): void {
        var ramqInfo = new RAMQInfo().FromResponse(response);

        store.PAListActions.PrintLabelCompleteOne(store.patientToCompleteWithRamqInfo, ramqInfo, "completeOne");
    }

    private SaveFilterSettings(response: ServerEventWithThen<any, () => void>): void {
        response.then?.();
    }
}

export { PAListResponseHandler };
